import { useSetSearchParam } from '@/hooks/router';
import { DashboardVariation } from '@/types/graphql';
import { gql } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo } from 'react';

import { DEFAULT_DASHBOARD_VARIATION } from '../../../../types/organization';
import { ContainerDefinitionKey } from '../definitions';
import { CARE_OPTIONS_FOR_KIDS_DASHBOARD_CONFIG } from './careOptionsForKids';
import { HOSPICE_DEMO_DASHBOARD_CONFIG } from './demo';
import { ELEANOR_HEALTH_DASHBOARD_CONFIG } from './eleanor';
import { HOME_HEALTH_DASHBOARD_CONFIG } from './homeHealth';
import { NORTH_STAR_DASHBOARD_CONFIG, NORTH_STAR_UAT_DASHBOARD_CONFIG } from './northStar';
import { PENN_DASHBOARD_CONFIG } from './penn';
import { PINNACLE_DASHBOARD_CONFIG, PINNACLE_UAT_DASHBOARD_CONFIG } from './pinnacle';
import { TELADOC_DASHBOARD_CONFIG } from './teladoc';
import { IDashboardVariationConfig } from './types';

// `?uat=1` lets a user preview their org's UAT dashboard variation.
// Orgs without a UAT counterpart ignore the param.
const UAT_QUERY_PARAM = 'uat';
const UAT_VARIATION_MAP: Partial<Record<DashboardVariation, DashboardVariation>> = {
    [DashboardVariation.PINNACLE_HEALTH]: DashboardVariation.PINNACLE_HEALTH_UAT,
    [DashboardVariation.NORTH_STAR]: DashboardVariation.NORTH_STAR_UAT,
};

// Fetch the dashboard variation for the current user's organization or return
// the default dashboard variation. `?uat=1` swaps to the UAT counterpart for
// orgs that have one.
const useDashboardVariation = (): DashboardVariation => {
    const [uatParam] = useSetSearchParam(UAT_QUERY_PARAM);

    const { data } = useSuspenseQuery<{
        currentOrganization: {
            id: string;
            dashboardVariation: DashboardVariation;
        } | null;
    }>(gql`
        query OrganizationDashboardVariation {
            currentOrganization {
                id
                dashboardVariation
            }
        }
    `);

    // Purposefully memoizing without dependencies to only evaluate once so that
    // dashboard variation does not change out from under us.
    const dashboardVariation = useMemo(() => {
        const orgVariation = data.currentOrganization?.dashboardVariation;
        if (!orgVariation) {
            console.info(
                'No dashboard variation set for organization, using default',
                DEFAULT_DASHBOARD_VARIATION,
            );
            return DEFAULT_DASHBOARD_VARIATION;
        }
        const uatVariation = UAT_VARIATION_MAP[orgVariation];
        if (uatParam === '1' && uatVariation) {
            return uatVariation;
        }
        return orgVariation;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return dashboardVariation;
};

export const DASHBOARD_VARIATION_CONFIG: Record<
    DashboardVariation,
    IDashboardVariationConfig | null
> = {
    [DashboardVariation.ADVENT_HEALTH]: null,
    [DashboardVariation.CARE_OPTIONS_FOR_KIDS]: CARE_OPTIONS_FOR_KIDS_DASHBOARD_CONFIG,
    [DashboardVariation.ELEANOR_HEALTH]: ELEANOR_HEALTH_DASHBOARD_CONFIG,
    [DashboardVariation.HOME_HEALTH_DEMO]: HOME_HEALTH_DASHBOARD_CONFIG,
    [DashboardVariation.HOSPICE_DEMO]: HOSPICE_DEMO_DASHBOARD_CONFIG,
    [DashboardVariation.JET_HEALTH]: null,
    [DashboardVariation.NOBLE_HEALTHCARE]: null,
    [DashboardVariation.PENN_MEDICINE]: PENN_DASHBOARD_CONFIG,
    [DashboardVariation.NORTH_STAR]: NORTH_STAR_DASHBOARD_CONFIG,
    [DashboardVariation.NORTH_STAR_UAT]: NORTH_STAR_UAT_DASHBOARD_CONFIG,
    [DashboardVariation.TELADOC]: TELADOC_DASHBOARD_CONFIG,
    [DashboardVariation.PINNACLE_HEALTH]: PINNACLE_DASHBOARD_CONFIG,
    [DashboardVariation.PINNACLE_HEALTH_UAT]: PINNACLE_UAT_DASHBOARD_CONFIG,
};

export const useDashboardVariationConfig = (): IDashboardVariationConfig & {
    variation: DashboardVariation;
} => {
    const dashboardVariation = useDashboardVariation();
    const config = DASHBOARD_VARIATION_CONFIG[dashboardVariation] ?? { containers: [] };
    return {
        ...config,
        variation: dashboardVariation,
    };
};

export const getDefaultEnabledContainerKeys = (
    variation: DashboardVariation,
): ContainerDefinitionKey[] => {
    return (
        DASHBOARD_VARIATION_CONFIG[variation]?.containers
            .filter((container) => container.defaultEnabled)
            .map((container) => container.key) ?? []
    );
};
